import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, inject } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { AccountObject, AccountType, UserRoles } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import {
  OnDestroyMixin,
  untilComponentDestroyed,
} from '@w11k/ngx-componentdestroyed'
import { AuthApiService } from 'src/api/auth'
import { ModalService } from 'src/shared/modal'
import { AddBuyersGroupComponent } from '../add-buyers-group/add-buyers-group.component'
import { CreateFromSharedGroupComponent } from '../create-from-shared-group/create-from-shared-group.component'
import { ShareBuyersGroupComponent } from '../share-buyers-group/share-buyers-group.component'
import { BuyersGroup } from './buyers-groups.service'

type BuyerIssue = 'archived' | 'converted' | 'notAccessible'

const ISSUE_MESSAGE: Record<BuyerIssue, (subject: string, verb: string) => string> = {
  archived: (subject, verb) => `${subject} ${verb} been archived. Please remove from your buyer groups.`,
  converted: (subject, verb) => `${subject} ${verb} been converted to a different company type (supplier or service provider). Please remove from buyer group.`,
  notAccessible: (subject) => `You are no longer an Account Manager for ${subject}. Please remove from buyer group.`,
}

@Component({
  selector: 'tc-buyers-groups-item',
  templateUrl: './buyers-groups-item.component.html',
  styleUrls: ['./buyers-groups-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BuyersGroupsItemComponent extends OnDestroyMixin
  implements OnInit {
  private formBuilder = inject(FormBuilder)
  private modal = inject(ModalService)
  private AuthApi = inject(AuthApiService)

  @Input() buyersMap: Dictionary<DeepReadonly<AccountObject>>
  @Input() usersMap: Dictionary<{ fullname?: string }> = {}
  @Input() buyers: DeepReadonly<AccountObject>[]
  @Input() group: BuyersGroup
  @Input() groups: BuyersGroup[]
  @Input() badBuyersSet = new Set<string>()
  @Input() badBuyersMap: Record<string, string[]> = {}
  @Output() groupDeleted = new EventEmitter<BuyersGroup>()
  @Output() groupEdited = new EventEmitter<BuyersGroup>()
  @Output() groupCreated = new EventEmitter<BuyersGroup>()
  @Output() groupUpdated = new EventEmitter<{
    group: BuyersGroup
    callback: Function
  }>()
  itemFormGroup: FormGroup
  dirty: boolean
  isEditable: boolean

  constructor() {
    super()
    this.itemFormGroup = this.formBuilder.group({
      group_id: [null, Validators.required],
      buyers: [null, Validators.required],
      name: [null, Validators.required],
    })
  }


  ngOnInit(): void {
    this.itemFormGroup.patchValue(this.group)
    this.itemFormGroup.valueChanges
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => (this.dirty = true))
  }

  onBuyerRemoved(removedBuyer) {
    const buyers = this.itemFormGroup.value.buyers
    this.itemFormGroup.patchValue({
      buyers: buyers.filter((buyer) => buyer !== removedBuyer),
    })
  }

  updateGroup() {
    if (!this.itemFormGroup.valid) return
    if (this.isShared) {
      this.promptCreateFromShared(this.itemFormGroup.value.buyers)
      return
    }
    this.groupUpdated.emit({
      group: this.itemFormGroup.value,
      callback: () => {
        this.dirty = false
        this.isEditable = false
      },
    })
  }

  private promptCreateFromShared(buyers: string[]) {
    this.modal.openDialog(CreateFromSharedGroupComponent, { buyers })
      .subscribe((newGroup?: BuyersGroup) => {
        if (!newGroup) return
        this.groupCreated.emit(newGroup)
        this.itemFormGroup.patchValue(this.group)
        this.dirty = false
        this.isEditable = false
      })
  }
  deleteGroup() {
    this.groupDeleted.emit(this.group)
  }

  get isShared(): boolean {
    return !!this.group?.shared_by && !this.group?.is_forked
  }

  get sharedByName(): string {
    return this.usersMap?.[this.group?.shared_by]?.fullname ?? ''
  }

  shareGroup(event: Event) {
    event.stopPropagation()
    this.modal.openDialog(ShareBuyersGroupComponent, {
      group: this.group,
    }).subscribe((result?: { is_public?: boolean }) => {
      if (result?.is_public === undefined || result.is_public === !!this.group.is_public) return
      this.groupEdited.emit({ ...this.group, is_public: result.is_public })
    })
  }

  setEditable(event: Event) {
    event.stopPropagation()
    if (this.isShared) {
      this.promptCreateFromShared(this.itemFormGroup.value.buyers)
      return
    }
    this.isEditable = true
  }

  editGroup(event: Event) {
    event.stopPropagation()
    this.modal.openDialog(AddBuyersGroupComponent, {
      buyers: this.buyers,
      groups: this.groups,
      group: this.group,
      onSaved: (result: BuyersGroup) => {
        setTimeout(() => {
          this.group = result
          this.itemFormGroup.patchValue(result)
          this.dirty = false
          this.groupEdited.emit(result)
        })
      },
      onCreated: (newGroup: BuyersGroup) => {
        setTimeout(() => this.groupCreated.emit(newGroup))
      },
    }, { width: '900px' })
  }

  getBuyerColor(buyer: string): string {
    if (this.getBuyerIssue(buyer)) return ''
    return this.badBuyersSet.has(buyer) ? 'warn' : 'primary'
  }

  getBuyerTooltip(buyer: string): string {
    return this.badBuyersMap[buyer]?.join('\n\n') ?? ''
  }

  getBuyerIssue(buyer: string): BuyerIssue | null {
    const account = this.buyersMap?.[buyer]
    if (!account) return null
    if (account.archived) return 'archived'
    if (account.type !== AccountType.BUYER) return 'converted'
    if (!this.hasAccess(account)) return 'notAccessible'
    return null
  }

  get issueMessages(): string[] {
    const namesByIssue = new Map<BuyerIssue, string[]>()
    for (const id of (this.itemFormGroup.value.buyers || []) as string[]) {
      const issue = this.getBuyerIssue(id)
      const name = this.buyersMap?.[id]?.name
      if (!issue || !name) continue
      const bucket = namesByIssue.get(issue) ?? []
      bucket.push(name)
      namesByIssue.set(issue, bucket)
    }
    return Array.from(namesByIssue, ([issue, names]) => {
      const subject = names.map((n) => `<strong>${n}</strong>`).join(', ')
      const verb = names.length === 1 ? 'has' : 'have'
      return ISSUE_MESSAGE[issue](subject, verb)
    })
  }

  private hasAccess(account: DeepReadonly<AccountObject>): boolean {
    const user = this.AuthApi.currentUser
    const adminRoles = [UserRoles.manager.id, UserRoles.superuser.id, UserRoles.administrator.id]
    if (adminRoles.includes(user.role)) return true
    return [account.manager, ...(account.managers || [])].includes(user.user_id)
  }
}
